import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import EColourPanel from '../../../interfaces/EColourPanel';
import { HTTPMethods } from '../../../services/API';
import { IMessageOptions } from '../../../yoello-lib/modules/API/useAPI';
import { useSnackbarMessages } from '../../../yoello-lib/modules/components/snackbar/SnackbarContext';
import { preferenceForm } from "../../../formSchemas/preference.form";
import { IReduxAppState } from "../../../reducers";
import DynamicForm from '../../../yoello-lib/modules/dynamicForms/DynamicForms';
import ListLoading from '../../util/Loading';
import { IMultiDialogChildren } from '../../dialogRoutes/MultiDialogRoute';
import { useDialogSetup } from '../../dialogRoutes/useDialogSetup';
import { useChangedDialog } from '../../../hooks/useChangedDialog';

import useCheckPermissions from "../../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../../const/permissions.const";
import { FormLoadingWrapper } from '../../util/FormLoadingWrapper';

export interface IPreference {
    enabled: true;
    id: string;
    name_internal: string;
    name_public: string;
    preferences: string[];
    serial_id: number;
    venue_id: string;
}

export interface IPreferenceFormComponent extends IMultiDialogChildren {
    onSubmit: (body: IPreference) => void;
    formData: IPreference | null;
    venueId: string;
    callAPI: (httpMethod: HTTPMethods, msgOptions?: IMessageOptions) => Promise<any>;
}

export const PreferenceFormComponent = (props: IPreferenceFormComponent) => {
    const { venueId, callAPI, onClose, onDialogClose } = props;
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const [isLoading, setIsLoading] = useState(false);
    const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);
    const { t } = useTranslation();
    const { formData, isLoading: isSetupLoading } = useDialogSetup(props, callAPI, "formData");
    const [formState, setFormState] = useState();
    const { hasFormChanged } = useChangedDialog();

    const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT);

    useEffect(() => {
        if (formData) {
            setFormState(mapData());
        }
        //eslint-disable-next-line
    }, [formData]);
    async function onSubmit(formdata: any) {
        try {
            setIsLoading(true);
            // API STUFF
            const object = {
                enabled: true,
                ...formData,
                ...formdata,
                venue_id: venueId,
                preferences: preferencesObjectIntoArray(formdata.preferences)
            };

            if (formData?.id) {
                await callAPI(HTTPMethods.PUT, { successCall: t("snackbar.updatePreferenceSuccess"), errorCall: t("snackbar.updatePreferenceFailed"), body: object, query: formData.id });
            } else {
                await callAPI(HTTPMethods.POST, { successCall: t("snackbar.savePreferenceSuccess"), errorCall: t("snackbar.savePreferenceFailed"), body: object });
            }
            setIsLoading(false);
            if (onDialogClose) {
                onDialogClose();
            } else {
                onClose();
            }

        }
        catch (error) {
            setIsLoading(false);
            sendSnackbarMessage(error as string, "error");
        }
    }
    function mapData() {
        if (formData) {
            const pref = [];
            const preferences = formData?.preferences;
            if (preferences) {
                for (const key of preferences) {
                    pref.push({ preference_name: key });
                }
            }
            return {
                ...formData,
                preferences: pref
            };
        } else {
            return {
                enabled: true
            };
        }
    }


    if (isLoading || isSetupLoading || (!formData && !props.isNewItem)) return <FormLoadingWrapper> <ListLoading /> </FormLoadingWrapper> ;

    return <div style={{ padding: "5px" }}>
        <DynamicForm
            onHandleSubmit={onSubmit}
            formData={formState}
            formRows={preferenceForm(t, props, isEposNow, props.isNewItem)}
            formKeyName="details"
            disableAll={!hasPermission}
            disableAllInteraction={onFailedInteraction}
            useFormOptions={
                {
                    reValidateMode: "onChange",
                    mode: 'onChange'
                }
            }
            hasDataChanged={hasFormChanged}
            isDialog={true}
            formButtons={{
                rightButton: { type: "submit", label: t('menu.preferences.submit'), color: EColourPanel.PRIMARY, disabled: isLoading || !hasPermission },
                leftButton: { type: "button", label: t('misc.cancel'), color: EColourPanel.PRIMARY, onClick: onClose }
            }}
        />
    </div>;
};


export function preferencesObjectIntoArray(preferenceObject: { [key: string]: { preference_name: string; }; }): string[] {
    const stringOfPreferences = [];
    const keys = Object.keys(preferenceObject);
    for (const key of keys) {
        if (preferenceObject[key].preference_name.trim().length === 0) {
            throw Error(`Invalid Preference Name`);
        }
        stringOfPreferences.push(preferenceObject[key].preference_name);
    }
    return stringOfPreferences;
}

export function createPreferencesObject() {
    return {
        preference_name: ""
    };
}
export default PreferenceFormComponent;